if (!customElements.get('product-trial')) {
  customElements.define(
    'product-trial',
    class ProductTrial extends HTMLElement {
      constructor() {
        super();
        this.config = JSON.parse(
          this.querySelector('[data-trial-product-config]').textContent
        );
        this.currentFragrance = this.config.defaultFragrance;
        this.shouldSyncURL = false;
      }

      connectedCallback() {
        // Detect initial product from URL
        this.detectFromURL();

        // Bind fragrance pills
        this.querySelectorAll('[data-selector="fragrance"]').forEach((btn) => {
          btn.addEventListener('click', (e) => this.onFragranceChange(e));
        });

        this.querySelectorAll('.combined-product__read-more-btn').forEach((btn) => {
          btn.addEventListener('click', (e) => this.onReadMoreToggle(e));
        });

        if (!this._readMoreResizeBound) {
          this._readMoreResizeBound = true;
          window.addEventListener('resize', () => this.syncReadMoreButtons());
        }

        // Bind info modals (fragrance + trial)
        this.initInfoModal('fragrance');
        this.initInfoModal('trial');

        // Close modals on Escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.closeAllModals();
        });

        // Set initial state
        this.updatePills('fragrance', this.currentFragrance);
        this.updateView();

        // Init sticky ATC bar
        this.initStickyATC();
      }

      get currentKey() {
        return this.currentFragrance;
      }

      detectFromURL() {
        const path = window.location.pathname;
        if (!path.startsWith('/products/')) return;
        for (const [key, data] of Object.entries(this.config.products)) {
          if (data.handle && path.includes(data.handle)) {
            this.currentFragrance = key;
            this.shouldSyncURL = true;
            break;
          }
        }
      }

      onFragranceChange(e) {
        const newFragrance = e.currentTarget.dataset.value;
        if (newFragrance === this.currentFragrance) return;
        this.currentFragrance = newFragrance;
        this.updatePills('fragrance', newFragrance);
        this.updateView();
      }

      updatePills(selector, value) {
        this.querySelectorAll(`[data-selector="${selector}"]`).forEach(
          (btn) => {
            btn.classList.toggle(
              'combined-product__pill--active',
              btn.dataset.value === value
            );
          }
        );
      }

      updateView() {
        const key = this.currentKey;

        // Toggle all panels by data-product-key
        this.querySelectorAll('[data-product-key]').forEach((panel) => {
          const matches = panel.dataset.productKey === key;
          panel.hidden = !matches;
        });

        // Update URL
        this.updateURL();

        // Re-init gallery Swiper for newly visible panel
        this.reinitGallery(key);

        // Sync sticky bar state
        this.syncStickyDropdowns();
        this.updateStickyPrice();
        this.observeActiveBuyButton();
        this.syncJudgeMeWidgets();
        this.syncReadMoreButtons();
      }

      syncReadMoreButtons() {
        requestAnimationFrame(() => {
          this.querySelectorAll('.combined-product__read-more-btn').forEach((btn) => {
            const wrapper = btn.closest('[data-product-key]') || btn.parentElement;
            if (wrapper?.hidden) return;

            const desc = wrapper.querySelector('.product__description');
            if (!desc || desc.classList.contains('combined-product__description--expanded')) return;

            desc.classList.add('combined-product__description--truncated');
            const overflows = desc.scrollHeight > desc.clientHeight + 2;

            btn.classList.toggle('combined-product__read-more-btn--visible', overflows);
            if (!overflows) {
              desc.classList.remove('combined-product__description--truncated');
              btn.textContent = 'Read more';
            }
          });
        });
      }

      syncJudgeMeWidgets() {
        const productId = this.config.products[this.currentKey]?.id;
        if (productId == null || typeof window.updateJudgeMeWidgets !== 'function') return;
        window.updateJudgeMeWidgets(productId);
      }

      updateURL() {
        if (!window.location.pathname.startsWith('/products/')) return;
        if (!this.shouldSyncURL) return;
        const productData = this.config.products[this.currentKey];
        if (productData && productData.url) {
          window.history.replaceState({}, '', productData.url);
        }
      }

      initInfoModal(type) {
        const modal = this.querySelector(`[data-${type}-modal]`);
        if (!modal) return;
        this.querySelectorAll(`[data-${type}-modal-open]`).forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.openInfoModal(modal);
          });
        });
        modal.querySelectorAll(`[data-${type}-modal-close]`).forEach((el) => {
          el.addEventListener('click', () => this.closeInfoModal(modal));
        });
      }

      openInfoModal(modal) {
        if (!modal) return;
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        modal.querySelector('.info-modal__close')?.focus();
      }

      closeInfoModal(modal) {
        if (!modal) return;
        modal.hidden = true;
        document.body.style.overflow = '';
      }

      closeAllModals() {
        this.querySelectorAll('.info-modal').forEach((modal) => {
          this.closeInfoModal(modal);
        });
      }

      onReadMoreToggle(e) {
        const btn = e.currentTarget;
        const wrapper = btn.closest('[data-product-key]') || btn.parentElement;
        const descriptionEl = wrapper.querySelector('.product__description');
        if (!descriptionEl) return;

        const isExpanded = descriptionEl.classList.contains(
          'combined-product__description--expanded'
        );

        if (isExpanded) {
          descriptionEl.classList.remove('combined-product__description--expanded');
          descriptionEl.classList.add('combined-product__description--truncated');
          btn.textContent = 'Read more';
        } else {
          descriptionEl.classList.add('combined-product__description--expanded');
          btn.textContent = 'Read less';
        }
      }

      reinitGallery(key) {
        const panel = this.querySelector(
          `.combined-product__media-panel[data-product-key="${key}"]`
        );
        if (!panel) return;

        const slider = panel.querySelector('media-gallery-slider');
        if (slider && typeof slider.swiperInitialization === 'function') {
          slider.previousWidth = 0;
          slider.swiperInitialization();
        }

        const gallery = panel.querySelector('media-gallery');
        if (gallery) {
          const swiperEl = gallery.querySelector('.swiper');
          if (swiperEl && swiperEl.swiper) {
            swiperEl.swiper.update();
          }
        }
      }

      /* ========================================
         Sticky Add to Cart Bar
         ======================================== */
      initStickyATC() {
        this.stickyBar = this.querySelector('[data-sticky-atc]');
        if (!this.stickyBar) return;

        this.stickyFragrance = this.stickyBar.querySelector('[data-sticky-fragrance]');
        this.stickyPlan = this.stickyBar.querySelector('[data-sticky-plan]');
        this.stickyPrice = this.stickyBar.querySelector('[data-sticky-price]');
        this.stickyBtn = this.stickyBar.querySelector('[data-sticky-atc-btn]');

        // Set initial dropdown values
        this.syncStickyDropdowns();
        this.updateStickyPrice();

        // Bind dropdown changes
        if (this.stickyFragrance) {
          this.stickyFragrance.addEventListener('change', (e) => {
            const newFragrance = e.target.value;
            if (newFragrance === this.currentFragrance) return;
            this.currentFragrance = newFragrance;
            this.updatePills('fragrance', newFragrance);
            this.updateView();
            this.syncStickyDropdowns();
            this.updateStickyPrice();
          });
        }

        // Bind ATC button
        if (this.stickyBtn) {
          this.stickyBtn.addEventListener('click', () => this.onStickyAddToCart());
        }

        // Set up IntersectionObserver on the active buy panel
        this.initStickyObserver();
      }

      initStickyObserver() {
        if (!this.stickyBar) return;

        if (this.stickyObserver) {
          this.stickyObserver.disconnect();
        }

        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.stickyBar.classList.remove('sticky-atc--visible');
              } else {
                this.stickyBar.classList.add('sticky-atc--visible');
              }
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );

        this.observeActiveBuyButton();
      }

      observeActiveBuyButton() {
        if (!this.stickyObserver) return;
        this.stickyObserver.disconnect();

        const key = this.currentKey;
        const buyPanel = this.querySelector(
          `.combined-product__buy-panel[data-product-key="${key}"]`
        );
        if (!buyPanel) return;

        const submitBtn = buyPanel.querySelector('.product-form__submit');
        if (submitBtn) {
          this.stickyObserver.observe(submitBtn);
        }
      }

      syncStickyDropdowns() {
        if (this.stickyFragrance) {
          this.stickyFragrance.value = this.currentFragrance;
        }
        if (this.stickyPlan) {
          this.stickyPlan.value = 'onetime';
        }
      }

      updateStickyPrice() {
        if (!this.stickyPrice) return;
        const key = this.currentKey;
        const productData = this.config.products[key];
        if (!productData) return;

        const price = productData.price;
        const formatted = (price / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
        }); 
        this.stickyPrice.textContent = 'Total: ' + formatted;

        if (this.stickyBtn) {
          this.stickyBtn.disabled = !productData.available;
          this.stickyBtn.textContent = productData.available
            ? 'Add to Cart'
            : 'Sold Out';
        }
      }

      onStickyAddToCart() {
        const key = this.currentKey;
        const buyPanel = this.querySelector(
          `.combined-product__buy-panel[data-product-key="${key}"]`
        );
        if (!buyPanel) return;

        const form = buyPanel.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn && !submitBtn.disabled) {
          if (this.stickyBtn) {
            this.stickyBtn.classList.add('sticky-atc__button--loading');
            this.stickyBtn.textContent = 'Adding...';
          }
          submitBtn.click();
          setTimeout(() => {
            if (this.stickyBtn) {
              this.stickyBtn.classList.remove('sticky-atc__button--loading');
              const price = this.stickyPrice ? this.stickyPrice.textContent : '';
              this.stickyBtn.textContent = price ? 'Add — ' + price : 'Add to Cart';
            }
          }, 2000);
        }
      }
    }
  );
}
